// An ad click on a call, a lead or a job (BL-092, adClicks.get and leads.search): what the ad's URL
// carried, as label and value, in the office's words.

export interface AdClick {
  /** Where the click was kept: the company's website tag, or the AdTrack import. */
  source: 'site' | 'brook_crm';
  platform: string | null;
  clickId: string | null;
  clickKind: 'gclid' | 'msclkid' | null;
  campaign: string | null;
  keyword: string | null;
  matchtype: string | null;
  landingPage: string | null;
  at: string | null;
}

const PLATFORM_WORDS: Record<string, string> = {
  google: 'Google Ads',
  microsoft: 'Microsoft Ads',
  bing: 'Microsoft Ads',
};
/** The ads' match types as the URL sends them: e, p, b, or the word. */
const MATCH_WORDS: Record<string, string> = {
  e: 'exact match',
  exact: 'exact match',
  p: 'phrase match',
  phrase: 'phrase match',
  b: 'broad match',
  broad: 'broad match',
};

export const platformWord = (p: string | null): string | null =>
  p ? (PLATFORM_WORDS[p.toLowerCase()] ?? p) : null;

/** The keyword with its match type: “water heater”, exact match. */
export function keywordWords(c: Pick<AdClick, 'keyword' | 'matchtype'>): string | null {
  if (!c.keyword) return null;
  const m = c.matchtype ? (MATCH_WORDS[c.matchtype.toLowerCase()] ?? c.matchtype) : null;
  return m ? `“${c.keyword}”, ${m}` : `“${c.keyword}”`;
}

/** The page they landed on, without its scheme: plumber.example/water-heaters. */
export const pageWords = (url: string | null): string | null =>
  url ? url.replace(/^https?:\/\//, '').replace(/\/$/, '') : null;

/** One line for a closed disclosure: Google Ads, “water heater”. */
export function clickLine(c: AdClick): string {
  const parts = [platformWord(c.platform) ?? 'An ad', c.keyword ? `“${c.keyword}”` : (c.campaign ?? null)];
  return parts.filter(Boolean).join(', ');
}

/** The click's details, in order, each only when it's known. */
export function clickRows(c: AdClick, when: (iso: string) => string): [string, string][] {
  const rows: [string, string | null][] = [
    ['Ad', platformWord(c.platform)],
    ['Campaign', c.campaign],
    ['Keyword', keywordWords(c)],
    ['Landing page', pageWords(c.landingPage)],
    [c.clickKind === 'msclkid' ? 'Microsoft click id' : 'Google click id', c.clickId],
    ['Clicked', c.at ? when(c.at) : null],
    ['Kept by', c.source === 'site' ? 'Your website’s tag' : 'AdTrack, imported'],
  ];
  return rows.filter((r): r is [string, string] => !!r[1]);
}
